/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  Maybe,
  useOrgUnitKpiDashboardQuery,
  OrgUnitKpiDataQuery,
} from "~/generated/graphql";
import { KpiType } from "~/constants";
import { formatMoney, percentage, convertToPercent } from "~/tools";

export function mapTags(typeName = "default") {
  return [
    {
      color: "success",
      text: typeName,
    },
  ];
}

export function getProgress({
  budget,
  forecast,
  currentCost,
}: {
  budget: number;
  forecast: number;
  currentCost: number;
}): {
  progress: number;
  isForecast: boolean;
} {
  const isForecast = forecast > 0;
  const baseline = isForecast ? forecast : currentCost;

  return { progress: Math.round(percentage(baseline, budget)), isForecast };
}

type Data = ReturnType<typeof useOrgUnitKpiDashboardQuery>["data"];

export function mapOrgUnitKpiData(data: Data, currency: string) {
  const { overallWaste, nonProdHours } = data?.orgUnitV2?.kpis ?? {};

  const overallWasteDetails = overallWaste?.details;
  const overallWasteCurrentTarget = overallWasteDetails?.targets?.currentTarget;
  const overallWasteTotalWaste =
    overallWasteDetails?.metrics?.latestTimePoint?.additionalFields?.find(
      ({ key }) => key === "wasteCost"
    )?.value;

  const nonProdHoursDetails = nonProdHours?.details;
  const nonProdHoursCurrentTarget = nonProdHoursDetails?.targets?.currentTarget;
  const nonProdHoursTotalWaste =
    nonProdHoursDetails?.metrics?.latestTimePoint?.additionalFields?.find(
      ({ key }) => key === "dailyShutdownHours"
    )?.value;

  return {
    overallWaste: {
      value: overallWasteDetails?.metrics?.latestTimePoint?.value,
      target: overallWasteCurrentTarget?.goal,
      additionalValue: formatMoney(overallWasteTotalWaste, currency),
    },
    nonProdHours: {
      value: nonProdHoursDetails?.metrics?.latestTimePoint?.value,
      target: nonProdHoursCurrentTarget?.goal,
      additionalValue: nonProdHoursTotalWaste
        ? `${nonProdHoursTotalWaste} h`
        : "",
    },
    overallWasteCurrentTarget: mapCurrentTarget(overallWasteCurrentTarget),
    nonProdHoursCurrentTarget: mapCurrentTarget(nonProdHoursCurrentTarget),
  };
}

type KpiTarget = {
  name?: string;
  goal?: number;
  startDate?: string;
  endDate?: string;
  id?: string;
  contactPerson?: Maybe<{ name: string }>;
};

function mapCurrentTarget(data?: Maybe<KpiTarget>) {
  if (data == null) {
    return undefined;
  }

  return {
    name: data.name ?? "",
    goal: data.goal ?? 0,
    startDate: data.startDate ?? "",
    endDate: data.endDate ?? "",
    id: data.id ?? "",
    contactPerson: data.contactPerson,
  };
}

export function mapOrgUnitKpi(kpiType: KpiType, data?: OrgUnitKpiDataQuery) {
  const kpiDetails = getOrgUnitKpiDetails(kpiType, data);

  if (kpiDetails == null) {
    return;
  }

  return {
    timePoints: kpiDetails.metrics?.values ?? [],
    targets: kpiDetails.targets?.targets?.map(mapTargets) ?? [],
  };
}

function mapTargets({ goal, startDate, endDate, name }: KpiTarget) {
  return {
    name: name ?? "",
    value: goal?.toString() ?? "",
    startDate: startDate ?? "",
    endDate: endDate ?? "",
  };
}

function getOrgUnitKpiDetails(kpiType: KpiType, data?: OrgUnitKpiDataQuery) {
  const kpis = data?.orgUnitV2?.kpis;

  switch (kpiType) {
    case KpiType.NON_PROD_HOURS:
      return kpis?.nonProdHours?.details;
    case KpiType.OVERALL_WASTE:
      return kpis?.overallWaste?.details;
    default:
      return;
  }
}

export function mapWorstChildOrgUnits(
  currency: string,
  data?: OrgUnitKpiDataQuery
) {
  const kpis = data?.orgUnitV2?.kpis;

  const worstWaste = kpis?.overallWaste?.worst?.orgUnits?.map(
    ({ orgUnit: { name, nid }, kpiValue, currentWaste }) => ({
      name,
      nid,
      value: `${convertToPercent(kpiValue)}%`,
      additionalValue: formatMoney(currentWaste, currency),
    })
  );

  const worstNonProdHours =
    kpis?.nonProdHours?.worst?.orgUnits?.map(
      ({ orgUnit: { name, nid }, kpiValue, hours }) => ({
        name,
        nid,
        value: `${convertToPercent(kpiValue)}%`,
        additionalValue: `${hours}h`,
      })
    ) ?? [];

  return worstWaste ?? worstNonProdHours;
}
